import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getSearchAnalyticsAPI, getSearchAnalyticsBreakdownAPI } from '../operations/planService';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Button,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  CircularProgress,
  Menu,
} from '@mui/material';
import {
  Visibility,
  FilterAlt,
  FilterAltOff,
  CalendarMonth,
  ArrowDropDown,
} from '@mui/icons-material';

/* ================= CONSTANTS ================= */
const INITIAL_PAGE_SIZE = 10;


/* ================= TYPES ================= */
interface SourceDetail {
  source: string;
  totalSearches: number;
  booking: number;
  tickets: number;
  ltbRatio: string;
}

interface MetaPlanRow {
  id: number;
  departure: string;
  arrival: string;
  outboundDate: string;
  inboundDate: string;
  tripType: string;
  totalSearches: number;
  booking: number;
  tickets: number;
  ltbRatio: string;
  sources: SourceDetail[];
}


/* ================= HELPERS ================= */
const MONTH_MAP: Record<string, string> = {
  Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
  Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
};
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "DD MMM YYYY" → "YYYY-MM-DD" (no timezone shift) */
const parseDMYtoISO = (dateStr: string): string => {
  const parts = dateStr.trim().split(' ');
  if (parts.length === 3 && MONTH_MAP[parts[1]]) {
    return `${parts[2]}-${MONTH_MAP[parts[1]]}-${parts[0].padStart(2, '0')}`;
  }
  return dateStr;
};

/** "YYYY-MM-DD" → "DD MMM YYYY" (no timezone shift) */
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  // already "DD MMM YYYY"
  if (/^\d{2} [A-Za-z]{3} \d{4}$/.test(dateStr)) return dateStr;
  // ISO "YYYY-MM-DD"
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const monthIdx = parseInt(parts[1], 10) - 1;
    return `${parts[2]} ${MONTH_SHORT[monthIdx]} ${parts[0]}`;
  }
  return dateStr;
};

const getDefaultOutbound = () => {
  const d = new Date();
  d.setDate(d.getDate() - 10);
  return d.toISOString().split('T')[0];
};

const getToday = () => new Date().toISOString().split('T')[0];

const getTripTypeColor = (tripType: string) => {
  switch (tripType) {
    case 'One Way':
      return { bg: '#dbeafe', color: '#2563eb' };
    case 'Round Trip':
      return { bg: '#ede9fe', color: '#7c3aed' };
    case 'Multi City':
      return { bg: '#fef3c7', color: '#d97706' };
    default:
      return { bg: '#f3f4f6', color: '#374151' };
  }
};

/* ================= COMPONENT ================= */
export default function MetaPlan() {
  const [openRowId, setOpenRowId] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_PAGE_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  // Filter states
  const [departureFilter, setDepartureFilter] = useState('');
  const [arrivalFilter, setArrivalFilter] = useState('');
  const [outboundDate, setOutboundDate] = useState('');
  const [inboundDate, setInboundDate] = useState('');
  const [tripTypeFilter, setTripTypeFilter] = useState('All');

  const inboundDateRef = useRef<HTMLInputElement>(null);

  const handleOutboundDateChange = (value: string) => {
    setOutboundDate(value);
    // Clear inbound if it's before the new outbound
    if (inboundDate && inboundDate < value) setInboundDate('');
    // Auto-open inbound date picker
    if (value) {
      setTimeout(() => {
        try { inboundDateRef.current?.showPicker(); } catch (_) {}
      }, 50);
    }
  };

  // Trip Type column header dropdown
  const [tripTypeMenuAnchor, setTripTypeMenuAnchor] = useState<HTMLElement | null>(null);

  // Applied filters (client-side — departure/arrival only)
  const [appliedRouteFilter, setAppliedRouteFilter] = useState({ departure: '', arrival: '' });

  // Track the dates that were last used for the API call
  const [lastFetchedDates, setLastFetchedDates] = useState({ outbound: getDefaultOutbound(), inbound: getToday() });

  const handleFilter = () => {
    // Date filter: call API only if dates changed since last fetch
    const datesChanged = outboundDate !== lastFetchedDates.outbound || inboundDate !== lastFetchedDates.inbound;
    if (datesChanged) {
      fetchData(outboundDate, inboundDate);
      setLastFetchedDates({ outbound: outboundDate, inbound: inboundDate });
    }
    // Departure/Arrival: client-side only, no API call
    setAppliedRouteFilter({ departure: departureFilter.trim().toUpperCase(), arrival: arrivalFilter.trim().toUpperCase() });
    setVisibleCount(INITIAL_PAGE_SIZE);
  };

  const handleClearFilter = () => {
    const defaultOutbound = getDefaultOutbound();
    const defaultInbound = getToday();
    setDepartureFilter('');
    setArrivalFilter('');
    setOutboundDate('');
    setInboundDate('');
    setTripTypeFilter('All');
    setAppliedRouteFilter({ departure: '', arrival: '' });
    // Re-fetch with default dates if user had applied different dates
    if (lastFetchedDates.outbound !== defaultOutbound || lastFetchedDates.inbound !== defaultInbound) {
      fetchData(defaultOutbound, defaultInbound);
      setLastFetchedDates({ outbound: defaultOutbound, inbound: defaultInbound });
    }
    setVisibleCount(INITIAL_PAGE_SIZE);
  };

  const [rows, setRows] = useState<MetaPlanRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewDataMap, setViewDataMap] = useState<Record<number, SourceDetail[]>>({});
  const [viewLoading, setViewLoading] = useState<number | null>(null);

  const fetchData = useCallback(async (startDate: string, endDate: string) => {
    try {
      setLoading(true);
      setViewDataMap({});
      setOpenRowId(null);
      const response = await getSearchAnalyticsAPI({ startDate, endDate, product: 'flight' });
      const data: any[] = response?.data?.analytics ?? [];
      const mapped: MetaPlanRow[] = (Array.isArray(data) ? data : []).map((item: any) => ({
        id: item.sNo,
        departure: item.departure ?? '',
        arrival: item.arrival ?? '',
        outboundDate: item.outboundDate ? parseDMYtoISO(item.outboundDate) : '',
        inboundDate: (item.inboundDate && item.inboundDate !== '-') ? parseDMYtoISO(item.inboundDate) : '',
        tripType: item.tripType ?? '',
        totalSearches: item.totalSearches ?? 0,
        booking: item.bookings ?? 0,
        tickets: item.tickets ?? 0,
        ltbRatio: item.ltbRatio ?? '0.00%',
        sources: Array.isArray(item.sources) ? item.sources.map((src: any) => ({
          source: src.source ?? '',
          totalSearches: src.totalSearches ?? 0,
          booking: src.bookings ?? 0,
          tickets: src.tickets ?? 0,
          ltbRatio: src.ltbRatio ?? '0.00%',
        })) : [],
      }));
      setRows(mapped);
    } catch (err) {
      console.error('[MetaPlan] fetchData failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(getDefaultOutbound(), getToday());
  }, [fetchData]);

  const toggleViewRow = async (row: MetaPlanRow) => {
    if (openRowId === row.id) {
      setOpenRowId(null);
      return;
    }
    setOpenRowId(row.id);
    if (viewDataMap[row.id] !== undefined) return;

    const payload = {
      sNo: row.id,
      departure: row.departure,
      arrival: row.arrival,
      outboundDate: row.outboundDate ? formatDate(row.outboundDate) : '',
      inboundDate: row.inboundDate ? formatDate(row.inboundDate) : '-',
      tripType: row.tripType,
      totalSearches: row.totalSearches,
      bookings: row.booking,
      tickets: row.tickets,
      ltbRatio: row.ltbRatio,
    };

    try {
      setViewLoading(row.id);
      const response = await getSearchAnalyticsBreakdownAPI(payload);
      const srcData: any[] = response?.data?.breakdown ?? [];
      const sources: SourceDetail[] = (Array.isArray(srcData) ? srcData : []).map((src: any) => ({
        source: src.source ?? '',
        totalSearches: src.totalSearches ?? 0,
        booking: src.bookings ?? src.booking ?? 0,
        tickets: src.tickets ?? 0,
        ltbRatio: src.ltbRatio ?? '0.00%',
      }));
      setViewDataMap((prev) => ({ ...prev, [row.id]: sources }));
    } catch (err) {
      console.error('[MetaPlan] source-breakdown failed:', err);
      setViewDataMap((prev) => ({ ...prev, [row.id]: [] }));
    } finally {
      setViewLoading(null);
    }
  };

  // Reset pagination when trip type filter changes (applied inline)
  useEffect(() => {
    setVisibleCount(INITIAL_PAGE_SIZE);
  }, [tripTypeFilter]);

  // Filter logic (dates are handled server-side via API; departure/arrival/tripType are client-side)
  const filteredData = rows.filter((row) => {
    if (appliedRouteFilter.departure && !row.departure.toUpperCase().includes(appliedRouteFilter.departure)) return false;
    if (appliedRouteFilter.arrival && !row.arrival.toUpperCase().includes(appliedRouteFilter.arrival)) return false;
    if (tripTypeFilter !== 'All' && row.tripType !== tripTypeFilter) return false;
    return true;
  });

  const visibleRows = filteredData.slice(0, visibleCount);
  const hasMoreRows = visibleCount < filteredData.length;

  // Auto-fill: after every render, if neither the window nor the table container is
  // scrollable yet (content too short to produce a scrollbar), load the next batch
  // automatically. Stops as soon as scrolling becomes possible.
  useEffect(() => {
    if (loadingRef.current || visibleCount >= filteredData.length) return;

    const pageScrollable = document.documentElement.scrollHeight > window.innerHeight;
    const container = tableContainerRef.current;
    const containerScrollable = container ? container.scrollHeight > container.clientHeight : false;

    if (!pageScrollable && !containerScrollable) {
      loadingRef.current = true;
      setIsLoadingMore(true);
      setTimeout(() => {
        setVisibleCount((prev) => prev + INITIAL_PAGE_SIZE);
        setIsLoadingMore(false);
        loadingRef.current = false;
      }, 300);
    }
  }, [visibleCount, filteredData.length]);

  // Scroll-to-load — handles two cases:
  // 1. Window scroll: when 10 rows don't fill the table (no table scrollbar), the page scrolls
  // 2. Container scroll: once enough rows are loaded, the table gets its own scrollbar
  useEffect(() => {
    const doLoadMore = () => {
      if (loadingRef.current || visibleCount >= filteredData.length) return;
      loadingRef.current = true;
      setIsLoadingMore(true);
      setTimeout(() => {
        setVisibleCount((prev) => prev + INITIAL_PAGE_SIZE);
        setIsLoadingMore(false);
        loadingRef.current = false;
      }, 400);
    };

    // Fires when the user scrolls the page (window-level scroll)
    const onWindowScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total - scrolled <= 200) doLoadMore();
    };

    // Fires when the table container itself has a scrollbar and the user scrolls within it
    const onContainerScroll = () => {
      const container = tableContainerRef.current;
      if (!container) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 150) doLoadMore();
    };

    window.addEventListener('scroll', onWindowScroll, { passive: true });
    const container = tableContainerRef.current;
    if (container) container.addEventListener('scroll', onContainerScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onWindowScroll);
      if (container) container.removeEventListener('scroll', onContainerScroll);
    };
  }, [filteredData.length, visibleCount]);

  return (
    <Box className="transactionList">
      {/* Header */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Meta Plan
      </Typography>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'flex-end',
          gap: 2,
          mb: 4,
          width: '100%',
          backgroundColor: '#f3f4f6',
          borderRadius: 1,
          padding: '16px 20px',
        }}
      >
        <FilterAlt sx={{ color: '#6b7280', mb: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>Outbound Date</Typography>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <CalendarMonth sx={{ position: 'absolute', left: 8, color: '#9ca3af', fontSize: 18, pointerEvents: 'none', zIndex: 1 }} />
            <input
              type="date"
              value={outboundDate}
              onChange={(e) => handleOutboundDateChange(e.target.value)}
              style={{
                height: 40,
                padding: '0 12px 0 32px',
                border: '1px solid #00000026',
                borderRadius: 1,
                fontSize: 14,
                outline: 'none',
                width: 160,
                boxSizing: 'border-box',
                backgroundColor: '#fff',
                color: outboundDate ? 'inherit' : 'rgba(0,0,0,0.4)',
              }}
            />
          </Box>
        </Box>
        <Typography sx={{ color: '#9ca3af', fontSize: 14, mb: 1 }}>—</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#6b7280' }}>Inbound Date</Typography>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <CalendarMonth sx={{ position: 'absolute', left: 8, color: '#9ca3af', fontSize: 18, pointerEvents: 'none', zIndex: 1 }} />
            <input
              ref={inboundDateRef}
              type="date"
              value={inboundDate}
              min={outboundDate || undefined}
              onChange={(e) => setInboundDate(e.target.value)}
              style={{
                height: 40,
                padding: '0 12px 0 32px',
                border: '1px solid #00000026',
                borderRadius: 1,
                fontSize: 14,
                outline: 'none',
                width: 160,
                boxSizing: 'border-box',
                backgroundColor: '#fff',
                color: inboundDate ? 'inherit' : 'rgba(0,0,0,0.4)',
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#6b7280' }}>Departure</Typography>
          <input
            type="text"
            placeholder="DEL"
            value={departureFilter}
            onChange={(e) => setDepartureFilter(e.target.value)}
            className="inputField"
            style={{
              width: 160,
              height: 40,
              padding: '0 12px',
              border: '1px solid #00000026',
              borderRadius: 1,
              fontSize: 14,
              outline: 'none',
              textTransform: 'uppercase',
              boxSizing: 'border-box',
              backgroundColor: '#fff',
            }}
          />
        </Box>
        <Typography sx={{ color: '#9ca3af', fontSize: 14, mb: 1 }}>—</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#6b7280' }}>Arrival</Typography>
          <input
            type="text"
            placeholder="DXB"
            value={arrivalFilter}
            onChange={(e) => setArrivalFilter(e.target.value)}
            className="inputField"
            style={{
              width: 160,
              height: 40,
              padding: '0 12px',
              border: '1px solid #00000026',
              borderRadius: 1,
              fontSize: 14,
              outline: 'none',
              textTransform: 'uppercase',
              boxSizing: 'border-box',
              backgroundColor: '#fff',
            }}
          />
        </Box>

        

        {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#6b7280' }}>Trip Type</Typography>
          <FormControl size="small" sx={{ minWidth: 170, backgroundColor: '#fff', '& .MuiOutlinedInput-root': { height: 40 } }}>
            <Select
              className="leftFilterBox"
              value={tripTypeFilter}
              onChange={(e) => setTripTypeFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="All">All Trip Types</MenuItem>
              <MenuItem value="One Way">One Way</MenuItem>
              <MenuItem value="Round Trip">Round Trip</MenuItem>
              <MenuItem value="Multi City">Multi City</MenuItem>
            </Select>
          </FormControl>
        </Box> */}

        <Button
          variant="contained"
          onClick={handleFilter}
          sx={{
            backgroundColor: '#272727',
            '&:hover': { backgroundColor: '#1a1a1a' },
            textTransform: 'none',
            fontWeight: 600,
            height: 40,
            px: 3,
            minWidth: 'auto',
            borderRadius: 1,
            ml: 2
          }}
        >
          Filter
        </Button>

        <Tooltip title="Clear Filter">
          <IconButton
            onClick={handleClearFilter}
            sx={{
              border: '1px solid #272727',
              color: '#272727',
              borderRadius: 1,
              height: 40,
              width: 40,
              ml: 1,
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            <FilterAltOff fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Table */}
      <Box
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <TableContainer
          ref={tableContainerRef}
          sx={{
            maxHeight: '75vh',
            overflowY: 'auto',
            backgroundColor: '#ffffff',
          }}
        >
          <Table stickyHeader>
            <TableHead
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: '#f1f3f4',
              }}
            >
              <TableRow
                sx={{
                  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
                }}
              >
                {['S.NO.', 'DEPARTURE', 'ARRIVAL', 'OUTBOUND DATE', 'INBOUND DATE', 'TRIP TYPE', 'TOTAL SEARCHES', 'BOOKING', 'TICKETS', 'LTB RATIO', 'ACTIONS'].map(
                  (label) => {
                    if (label === 'TRIP TYPE') {
                      const isFiltered = tripTypeFilter !== 'All';
                      return (
                        <TableCell
                          key={label}
                          align="center"
                          onClick={(e) => setTripTypeMenuAnchor(e.currentTarget)}
                          sx={{
                            backgroundColor: '#f1f3f4',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                            cursor: 'pointer',
                            userSelect: 'none',
                            width: 140,
                            minWidth: 140,
                            maxWidth: 140,
                            '&:hover': { backgroundColor: '#e5e7eb' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, width: '100%' }}>
                            <FilterAlt sx={{ fontSize: 14, color: isFiltered ? '#2563eb' : 'transparent' }} />
                            <span style={{ color: isFiltered ? '#2563eb' : 'inherit' }}>{label}</span>
                            <ArrowDropDown sx={{ fontSize: 18, color: isFiltered ? '#2563eb' : '#6b7280' }} />
                          </Box>
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell
                        key={label}
                        align="center"
                        sx={{
                          backgroundColor: '#f1f3f4',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                        }}
                      >
                        {label}
                      </TableCell>
                    );
                  }
                )}
              </TableRow>

              {/* Trip Type column header dropdown */}
              <Menu
                anchorEl={tripTypeMenuAnchor}
                open={Boolean(tripTypeMenuAnchor)}
                onClose={() => setTripTypeMenuAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                slotProps={{
                  paper: {
                    sx: {
                      minWidth: 170,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      borderRadius: 1,
                    },
                  },
                }}
              >
                {(['All', 'One Way', 'Round Trip', 'Multi City'] as const).map((option) => (
                  <MenuItem
                    key={option}
                    selected={tripTypeFilter === option}
                    onClick={() => {
                      setTripTypeFilter(option);
                      setTripTypeMenuAnchor(null);
                      setVisibleCount(INITIAL_PAGE_SIZE);
                    }}
                    sx={{
                      fontSize: 14,
                      fontWeight: tripTypeFilter === option ? 700 : 400,
                      color: tripTypeFilter === option ? '#2563eb' : 'inherit',
                    }}
                  >
                    {option === 'All' ? 'All Trip Types' : option}
                  </MenuItem>
                ))}
              </Menu>
            </TableHead>

            <TableBody>
              {visibleRows.map((row, idx) => {
                const isOpen = openRowId === row.id;
                const tripColor = getTripTypeColor(row.tripType);

                return (
                  <React.Fragment key={row.id}>
                    {/* Main Row */}
                    <TableRow hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                      <TableCell align="center">
                        {idx + 1}
                      </TableCell>
                      <TableCell align="center">{row.departure}</TableCell>
                      <TableCell align="center">{row.arrival}</TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
                          <CalendarMonth fontSize="small" sx={{ color: '#059669' }} />
                          {formatDate(row.outboundDate)}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
                          {row.inboundDate && <CalendarMonth fontSize="small" sx={{ color: '#059669' }} />}
                          {formatDate(row.inboundDate)}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.tripType}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            backgroundColor: tripColor.bg,
                            color: tripColor.color,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {row.totalSearches.toLocaleString()}
                      </TableCell>
                      <TableCell align="center">{row.booking}</TableCell>
                      <TableCell align="center">{row.tickets}</TableCell>
                      <TableCell align="center">
                        {row.ltbRatio}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => toggleViewRow(row)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Collapsible Detail Row */}
                    <TableRow>
                      <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#f9fafb' }}>
                            <Box sx={{ bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                              <Table size="small" sx={{ maxWidth: '700px' }}>
                                <TableHead sx={{ backgroundColor: '#f1f3f4' }}>
                                  <TableRow>
                                    <TableCell align="left" sx={{ fontWeight: 700, pl: 3 }}>SOURCE</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>TOTAL SEARCHES</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>BOOKING</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>TICKETS</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, pr: 3 }}>LTB RATIO</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {viewLoading === row.id ? (
                                    <TableRow>
                                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={24} sx={{ color: '#272727' }} />
                                      </TableCell>
                                    </TableRow>
                                  ) : viewDataMap[row.id] !== undefined && viewDataMap[row.id].length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#9ca3af', fontSize: 13 }}>
                                        No breakdown data available
                                      </TableCell>
                                    </TableRow>
                                  ) : (viewDataMap[row.id] ?? []).map((src) => (
                                    <TableRow key={src.source} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                      <TableCell align="left" sx={{ pl: 3, textTransform: 'capitalize' }}>{src.source}</TableCell>
                                      <TableCell align="center">{src.totalSearches.toLocaleString()}</TableCell>
                                      <TableCell align="center">{src.booking}</TableCell>
                                      <TableCell align="center">{src.tickets}</TableCell>
                                      <TableCell align="center" sx={{ pr: 3 }}>{src.ltbRatio}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {/* Initial API loading spinner */}
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {/* Loading more spinner (scroll pagination) */}
              {isLoadingMore && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 2, border: 0 }}>
                    <CircularProgress size={24} sx={{ color: '#272727' }} />
                  </TableCell>
                </TableRow>
              )}

              {/* All records loaded message */}
              {!loading && !isLoadingMore && !hasMoreRows && filteredData.length >= INITIAL_PAGE_SIZE && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 2, border: 0, color: '#9ca3af', fontSize: 13 }}>
                    No more records
                  </TableCell>
                </TableRow>
              )}

              {!loading && filteredData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 8, fontWeight: 600, color: 'text.secondary' }}>
                    No data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
